import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveApiBase } from '../../lib/apiBase';

// ─── Live-driven plan matrix (integration fix/plan-matrix-integration) ───────
// REAL backend contract (feat/public-plan-matrix, tests passing):
//   GET /api/public/plan-matrix  (no auth, envelope)
//   -> { success: true, data: {
//          plans: [{ id, name, scope: 'unit'|'user'|'condo',
//                    modules: { [moduleId]: { status, limit } } }],
//                    // status: 'included' | 'limit' | 'excluded' | 'na'
//                    // 'limit' -> numeric limit; -1 means unlimited (backend
//                    // already maps -1 to 'included', guarded client-side too)
//          modules: [{ id, label, scope }],
//          warnings: string[] } }
// Beta modules are omitted server-side (feature_flags module:<id> is_beta);
// unchecking a beta module makes its row appear within the 60s flag cache.
// No prices exist anywhere in the payload and none are ever rendered here.
//
// Column mapping is EXPLICIT by plan id (no token guessing — the backend's
// home-free plan id is literally `free`, which broke the old matcher):
//   free -> "Free (Home)" | home_plus -> "Home+"
//   home_ultra + home_unlimited -> merged "HomeUltra / Unlimited" (union)
//   condo_free -> "Free Condo"
//   condo_small..condo_xxl -> merged "Paid Condo" (union across tiers)
// Unknown/extra plans surface as their own column, never hidden.
// Free columns read "Disponible" + CTA; paid columns read "Próximamente" +
// "Oferta por tiempo limitado al lanzarse". Prices are NEVER shown.
// Limits are CONCURRENT-ACTIVE counts (Jaime, 2026-10-05): the cell shows the
// bare number + asterisk; one footnote explains the semantics.

type CellStatus = 'included' | 'limit' | 'excluded' | 'na';

interface PlanCell {
  status: CellStatus;
  limit?: number | null;
}
interface ApiPlan {
  id: string;
  name: string;
  scope?: string | null;
  modules: Record<string, PlanCell>;
}
interface ApiModule {
  id: string;
  label: string;
  scope?: string | null;
}
interface PlanMatrixData {
  plans: ApiPlan[];
  modules: ApiModule[];
  warnings?: string[];
}

interface MatrixColumn {
  id: string;
  name: string;
  available: boolean; // free plans -> "Disponible" + CTA; paid -> "Próximamente"
  planIds: string[];
}

interface DisplayCell {
  kind: CellStatus;
  limit: number | null;
}

// Free plans get the "Disponible" badge + register CTA; everything else is a
// paid ("Próximamente") column. Unknown plans default to paid so we never
// advertise a CTA for a plan we don't know is free.
const FREE_PLAN_IDS = new Set(['free', 'condo_free']);
const PAID_CONDO_IDS = ['condo_small', 'condo_medium', 'condo_large', 'condo_xl', 'condo_xxl'];

const LIMIT_FOOTNOTE = '* Los límites son de elementos activos simultáneos; los cerrados no cuentan.';

function buildColumns(plans: ApiPlan[]): MatrixColumn[] {
  const byId = new Map(plans.map(p => [p.id, p]));
  const used = new Set<string>();
  const cols: MatrixColumn[] = [];

  const takeOne = (id: string, name: string) => {
    const p = byId.get(id);
    if (!p) return;
    used.add(id);
    cols.push({ id, name, available: FREE_PLAN_IDS.has(id), planIds: [id] });
  };
  const merge = (ids: string[], name: string) => {
    const found = ids.map(id => byId.get(id)).filter((p): p is ApiPlan => !!p);
    if (found.length === 0) return;
    for (const p of found) used.add(p.id);
    cols.push({ id: ids.join('+'), name, available: false, planIds: found.map(p => p.id) });
  };

  takeOne('free', 'Free (Home)');
  takeOne('home_plus', 'Home+');
  merge(['home_ultra', 'home_unlimited'], 'HomeUltra / Unlimited');
  takeOne('condo_free', 'Free Condo');
  merge(PAID_CONDO_IDS, 'Paid Condo');

  // Tolerant fallback: any plan the explicit mapping didn't consume gets its
  // own column so new server-side plans still surface instead of hiding.
  for (const p of plans) {
    if (used.has(p.id)) continue;
    cols.push({ id: p.id, name: p.name || p.id, available: FREE_PLAN_IDS.has(p.id), planIds: [p.id] });
  }
  return cols;
}

// Union across a column's member plans (used by the merged columns):
//   any 'included' -> included; else any numeric 'limit' -> the most generous
//   (max) limit; else any 'excluded' -> excluded; otherwise 'na'.
function unionCell(planMap: Map<string, ApiPlan>, planIds: string[], moduleId: string): DisplayCell {
  const cells = planIds
    .map(pid => planMap.get(pid)?.modules?.[moduleId])
    .filter((c): c is PlanCell => !!c);
  if (cells.some(c => c.status === 'included' || (c.status === 'limit' && c.limit === -1))) {
    return { kind: 'included', limit: null };
  }
  const limits = cells
    .filter(c => c.status === 'limit' && typeof c.limit === 'number' && Number.isFinite(c.limit) && c.limit >= 0)
    .map(c => c.limit as number);
  if (limits.length > 0) return { kind: 'limit', limit: Math.max(...limits) };
  if (cells.some(c => c.status === 'excluded')) return { kind: 'excluded', limit: null };
  return { kind: 'na', limit: null };
}

function CellValue({ cell }: { cell: DisplayCell }) {
  if (cell.kind === 'na') {
    return <span className="text-gray-300" aria-label="No aplica">—</span>;
  }
  if (cell.kind === 'excluded') {
    return <span className="text-gray-300" aria-label="No incluido">✗</span>;
  }
  if (cell.kind === 'limit') {
    return (
      <span className="font-semibold text-gray-900">
        {cell.limit}<sup>*</sup>
      </span>
    );
  }
  return <span className="text-[#2bb8c5] font-semibold" aria-label="Incluido">✓</span>;
}

function StatusBadge({ available }: { available: boolean }) {
  return available
    ? <span className="inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-[#36CCD8]/10 text-[#2bb8c5]">Disponible</span>
    : <span className="inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Próximamente</span>;
}

function Skeleton() {
  return (
    <div className="animate-pulse">
      <div className="hidden sm:block h-64 rounded-2xl border border-gray-200 bg-white">
        <div className="h-14 border-b border-gray-100 rounded-t-2xl bg-gray-100/70" />
        {[...Array(6)].map((_, i) => <div key={i} className="h-10 border-b border-gray-100 bg-gray-50/60" />)}
      </div>
      <div className="sm:hidden space-y-4">
        {[...Array(3)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-gray-100/70" />)}
      </div>
    </div>
  );
}

function EmptyState({ error }: { error?: boolean }) {
  return (
    <div className="text-center py-12 px-6 rounded-2xl border border-gray-200 bg-white">
      <p className="text-gray-900 font-semibold mb-2">{error ? 'No pudimos cargar la comparación de planes.' : 'Aún no hay planes configurados.'}</p>
      <p className="text-gray-500 text-sm">Recarga la página en unos minutos.</p>
    </div>
  );
}

// Module-level cache: the landing page remounts on navigation; one fetch per
// browser session is enough and keeps the pricing table fast to scroll to.
let matrixCache: PlanMatrixData | null = null;

export default function PlanMatrix() {
  const [data, setData] = useState<PlanMatrixData | null>(matrixCache);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (data) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${resolveApiBase()}/public/plan-matrix`, { headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = (await r.json()) as { data?: PlanMatrixData };
        const d = json?.data;
        if (!d || !Array.isArray(d.plans) || !Array.isArray(d.modules) || d.plans.length === 0) {
          throw new Error('plan-matrix: vacío o forma inesperada');
        }
        matrixCache = d;
        if (!cancelled) { setData(d); setError(false); }
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [data]);

  if (error && !data) return <EmptyState error />;
  if (!data) return <Skeleton />;

  const columns = buildColumns(data.plans);
  const planMap = new Map(data.plans.map(p => [p.id, p]));
  const modules = data.modules;
  if (columns.length === 0 || modules.length === 0) return <EmptyState />;

  return (
    <div>
      {/* Tablet/desktop: comparison table, limits VISIBLE per cell */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full min-w-[760px] bg-white rounded-2xl border border-gray-200 overflow-hidden text-sm">
          <thead>
            <tr>
              <th className="text-left px-5 py-4 text-xs font-semibold tracking-widest uppercase text-gray-400 border-b border-gray-100">Módulo</th>
              {columns.map(c => (
                <th key={c.id} className="px-5 py-4 border-b border-gray-100 text-left">
                  <p className="font-semibold text-gray-900">{c.name}</p>
                  <div className="mt-1"><StatusBadge available={c.available} /></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.map((m, i) => (
              <tr key={m.id} className={i % 2 === 1 ? 'bg-gray-50/60' : ''}>
                <td className="px-5 py-3.5 font-medium text-gray-900 border-b border-gray-100">{m.label}</td>
                {columns.map(c => {
                  const cell = unionCell(planMap, c.planIds, m.id);
                  return (
                    <td key={c.id} className="px-5 py-3.5 border-b border-gray-100">
                      <CellValue cell={cell} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-400 mt-2">{LIMIT_FOOTNOTE}</p>
      </div>

      {/* Paid plans: Próximamente + limited-time offer wording (no prices, ever) */}
      <p className="hidden sm:block text-center text-sm text-gray-500 mt-6">
        Los planes de pago llegan <strong>próximamente</strong> y se lanzarán con una <strong>oferta por tiempo limitado</strong>. Sin precios hasta entonces.
      </p>

      {/* Phones: stacked plan cards, same data (na modules omitted per column) */}
      <div className="sm:hidden space-y-6">
        {columns.map(c => {
          const visible = modules.filter(m => unionCell(planMap, c.planIds, m.id).kind !== 'na');
          return (
            <div key={c.id} className={`rounded-2xl border p-6 ${c.available ? 'border-[#36CCD8]/50 bg-white' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center justify-between mb-4">
                <p className="font-semibold text-gray-900">{c.name}</p>
                <StatusBadge available={c.available} />
              </div>
              <ul className="space-y-2.5 text-sm">
                {visible.map(m => {
                  const cell = unionCell(planMap, c.planIds, m.id);
                  const dimmed = cell.kind === 'excluded';
                  return (
                    <li key={m.id} className="flex items-start justify-between gap-3">
                      <span className={dimmed ? 'text-gray-400' : 'text-gray-700'}>{m.label}</span>
                      <CellValue cell={cell} />
                    </li>
                  );
                })}
              </ul>
              {c.available ? (
                <Link to="/register" className="mt-6 block text-center bg-[#36CCD8] hover:bg-[#2bb8c5] text-white font-semibold px-6 py-3.5 rounded-full text-sm">Comenzar gratis</Link>
              ) : (
                <p className="mt-6 text-center text-xs text-gray-500">Oferta por tiempo limitado al lanzarse.</p>
              )}
            </div>
          );
        })}
        <p className="text-xs text-gray-400 text-center px-4">{LIMIT_FOOTNOTE}</p>
      </div>
    </div>
  );
}
